import React, {forwardRef, memo, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState} from 'react'

import {Icon, ListItem, P, Popover} from '../index.js'
import {FieldProps, FieldRef, InvalidProps} from './types.js'
import FieldBox, {isTextAppend} from './FieldBox.js'
import {fieldControlClass} from './fieldStyles.js'
import {field, getFieldCursor, getFieldTextColor, getTrailingPadding} from './styles.js'
import {boldMatch, escapeRegex, useFieldId} from './utils.js'
import type {PopoverRef} from '../overlays/Popover.js'

export type InputSuggestion = string | {value: string, label?: string}

// onChange gets the native change event while typing. When a suggestion is chosen it gets a plain object with only
// target.value, fromSuggestion, preventDefault and stopPropagation. It is not called when the value prop changes.
// Typed as a ChangeEvent so handlers typed for one can be passed; only rely on the fields above.
export type InputChangeEvent = React.ChangeEvent<HTMLInputElement> & {fromSuggestion?: boolean}

export interface InputProps extends FieldProps, InvalidProps, Pick<React.AriaAttributes, 'aria-label'> {
    value?: string | number
    /** With onChange the input is controlled: it shows value and a change only shows once the parent passes it back */
    onChange?: (event: InputChangeEvent) => void
    /** Style of the <input> element, e.g. font size or text alignment */
    inputStyle?: React.CSSProperties
    /** Style of the bordered box around the input, e.g. a fixed width */
    boxStyle?: React.CSSProperties
    id?: string
    type?: React.HTMLInputTypeAttribute
    required?: boolean
    infoIcon?: string
    placeholder?: string | number
    noPlaceholder?: boolean
    autoComplete?: string
    suggestions?: InputSuggestion[]
    suggestionFilter?: boolean
    onChooseSuggestion?: (event: {target: {value: string}}) => void
    blurOnChooseSuggestion?: boolean
    onFocus?: () => void
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
    onClick?: (event: React.MouseEvent<HTMLInputElement>) => void
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
    onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void
    prepend?: React.ReactNode
    append?: React.ReactNode
    inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
    maxLength?: number
    min?: number
    max?: number
    regex?: RegExp
    toUpperCase?: boolean
    capitalize?: boolean
    allowEmptyNumber?: boolean
}

export interface InputRef extends FieldRef {
    select: () => void
    getRef: () => HTMLInputElement | null
}

function capitalizeText(text: string) {
    text = text.trimStart()

    // Change the first character to uppercase and the rest to lowercase
    let formatted = text.toLowerCase()
    if (formatted.length > 0) {
        formatted = formatted[0].toUpperCase() + formatted.slice(1)
    }

    return formatted
}

// The suggestions shown in the popover, used for rendering as well as for the keyboard index and Enter
function filterSuggestions(suggestions: InputSuggestion[] | undefined, suggestionFilter: boolean | undefined, value: unknown) {
    if (!suggestions) {
        return []
    }

    if (!suggestionFilter) {
        return suggestions
    }

    const regex = new RegExp(`(${escapeRegex(String(value ?? ''))})`, 'gi')

    return suggestions.filter((suggestion) => (typeof suggestion === 'string' ? suggestion : suggestion.value).match(regex))
}

const Input = forwardRef<InputRef, InputProps>(function Input(props, ref) {
    const {style, inputStyle, boxStyle, id, type = 'text', required, label, info, infoIcon, placeholder, noPlaceholder, autoComplete, suggestions, disabled, readOnly, isInvalid, onFocus, onBlur, prepend, append, inputMode, onKeyDown, onKeyUp, suggestionFilter, maxLength} = props

    // Only used without onChange, then the input keeps its own value and follows props.value when that changes
    const [stateValue, setValue] = useState<any>(props.value ?? '')
    const value = props.onChange ? (props.value ?? '') : stateValue
    const [focused, setFocused] = useState(false)
    const [isInvalidState, setIsInvalidState] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [suggested, setSuggested] = useState(0)

    const fieldId = useFieldId(id)

    const inputRef = useRef<HTMLInputElement | null>(null)
    // The bordered box around the input, the suggestions popover is anchored to it
    const boxRef = useRef<HTMLDivElement | null>(null)
    const popoverRef = useRef<PopoverRef | null>(null)
    const popoverContentRef = useRef<HTMLDivElement | null>(null)

    // True while the last value change came from choosing a suggestion instead of typing.
    // Kept out of state on purpose: it must not trigger a render, it only guards reopening the popover.
    const suggestionChosenRef = useRef(false)

    // Props/state of the previous render, to compare against like componentDidUpdate(prevProps, prevState) did
    const prevRef = useRef<{suggestions: InputSuggestion[], value: any, suggested: number} | null>(null)

    // Timeout of chooseSuggestion, cleared on unmount
    const suggestionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const focus = () => {
        inputRef.current?.focus()
    }

    const blur = () => {
        inputRef.current?.blur()
    }

    useEffect(() => () => clearTimeout(suggestionTimeoutRef.current), [])

    useImperativeHandle(ref, () => ({
        focus,
        select: () => {
            inputRef.current.select()
        },
        blur,
        getRef: () => inputRef.current,
        setInvalid: (isInvalid = true) => {
            setIsInvalidState(isInvalid)
            focus()
        }
    }), [])

    useLayoutEffect(() => {
        const prev = prevRef.current
        prevRef.current = {suggestions: props.suggestions, value: props.value, suggested}

        if (!prev) {
            return
        }

        if (suggested !== prev.suggested) {
            const el = popoverContentRef.current?.querySelector<HTMLElement>(`#suggestion-${suggested}`)

            if (el) {
                const offsetTop = el.offsetTop
                const parentHeight = popoverContentRef.current.getBoundingClientRect().height

                const parentScrollOffsetTop = offsetTop - popoverContentRef.current.scrollTop

                if ((offsetTop > parentHeight && parentScrollOffsetTop > parentHeight) || (parentScrollOffsetTop < 0)) {
                    popoverContentRef.current.scrollTo({top: offsetTop})
                }
            }
        }
        if (props.suggestions?.toString() !== prev.suggestions?.toString()) {
            popoverContentRef.current?.scrollTo({top: 0})
            setSuggested(0)
        }

        if (focused && !suggestionChosenRef.current && JSON.stringify(props.suggestions) !== JSON.stringify(prev.suggestions)) {
            popoverRef.current?.show()
        }

        // Without onChange the own value follows props.value; onChange is not called for a value the parent passed itself
        if (!props.onChange && prev.value !== props.value) {
            setValue(props.value ?? '')
        }
    })

    const handleChange = (event: any) => {
        const {min, max, type, regex, toUpperCase, capitalize, suggestions, allowEmptyNumber = false} = props

        // A readOnly input still gets focus, make sure nothing (e.g. a suggestion) changes it
        if (props.disabled || props.readOnly) {
            return
        }

        setIsInvalidState(false)

        suggestionChosenRef.current = Boolean(event?.fromSuggestion)

        if (suggestions?.length > 0) {
            const exactMatch = filterSuggestions(suggestions, props.suggestionFilter, event.target.value).findIndex((suggestion) => {
                if (typeof suggestion === 'string') {
                    return suggestion === event.target.value
                }

                return suggestion.value === event.target.value
            })

            if (exactMatch > -1) {
                setSuggested(exactMatch)
            } else if (props.suggestionFilter) {
                // The shown list changes while typing, start at its first item again
                setSuggested(0)
            }
        }

        if (type === 'number') {
            if (event.target.value !== '' || !allowEmptyNumber) {
                event.target.value = parseInt(event.target.value || '0')
                if (parseInt(event.target.value) < min) {
                    event.target.value = min
                }

                if (parseInt(event.target.value) > max) {
                    event.target.value = max
                }

                event.target.valueAsNumber = event.target.value
            }
        }

        if (toUpperCase) {
            event.target.value = event.target.value.toUpperCase()
        }

        if (capitalize) {
            event.target.value = capitalizeText(event.target.value)
        }

        if (regex && typeof regex.test === 'function') {
            if (regex.test(event.target.value)) {
                change(event)
            }
        } else {
            change(event)
        }
    }

    // With onChange the parent passes the new value back as props.value, without it the input keeps the value itself
    const change = (event: InputChangeEvent) => {
        if (props.onChange) {
            props.onChange(event)
        } else {
            setValue(event.target.value)
        }
    }

    const chooseSuggestion = (value: string) => {
        const {onChooseSuggestion, blurOnChooseSuggestion} = props

        handleChange({
            target: {value},
            fromSuggestion: true,
            preventDefault: () => {},
            stopPropagation: () => {}
        })
        if (onChooseSuggestion) onChooseSuggestion({target: {value}})

        clearTimeout(suggestionTimeoutRef.current)
        suggestionTimeoutRef.current = setTimeout(() => {
            if (blurOnChooseSuggestion) {
                blur()
            } else {
                focus()
            }
            popoverRef.current?.close()
        }, 1)
    }

    const shownSuggestions = filterSuggestions(suggestions, suggestionFilter, value)
    // suggested can point past the end when typing filtered the list down
    const suggestedIndex = suggested < shownSuggestions.length ? suggested : 0

    const focusedState = focused || value || noPlaceholder || placeholder || value === 0

    const invalid = isInvalid || isInvalidState

    const textAppend = isTextAppend(append)
    // An icon append or the password eye is drawn over the input at the right, the invalid icon goes left of it
    const trailingWidth = (append && !textAppend) || type === 'password' ? 20 : 0
    // A text append takes its own room inside the box (see FieldBox), an icon or the eye is drawn over the input
    const paddingRight = textAppend ? 0 : type === 'password' ? field.appendWidth + field.paddingX : append ? field.appendWidth : field.paddingX

    // The box (border, background) is drawn by FieldBox, the input only places the text inside it
    const defaultInputStyle: React.CSSProperties = {
        fontSize: field.fontSize,
        width: '100%',
        height: '100%',
        minWidth: 0,
        paddingTop: field.paddingTop,
        paddingBottom: field.paddingBottom,
        paddingLeft: prepend ? field.prependWidth : field.paddingX,
        paddingRight: getTrailingPadding({invalid, trailingWidth, textAppend, padding: paddingRight}),
        outline: 'none',
        border: 'none',
        background: 'transparent',
        color: getFieldTextColor({disabled, readOnly}),
        cursor: getFieldCursor({disabled, readOnly}),
        ...inputStyle
    }

    return (
        <>
            <FieldBox
                label={label}
                info={info}
                infoIcon={infoIcon}
                floated={!!focusedState}
                focused={focused}
                invalid={invalid}
                disabled={disabled}
                readOnly={readOnly}
                prepend={prepend}
                append={append}
                trailingWidth={trailingWidth}
                controlId={fieldId}
                style={style}
                boxStyle={boxStyle}
                boxRef={boxRef}
            >
                <input
                    className={fieldControlClass}
                    style={defaultInputStyle}
                    id={fieldId}
                    type={showPassword ? '' : type}
                    placeholder={placeholder as string}
                    value={value === 0 ? 0 : (value || '')}
                    autoComplete={autoComplete}
                    disabled={disabled}
                    // Not disabled when readOnly, so the value can still be focused and selected
                    readOnly={readOnly}
                    required={required}
                    onFocus={() => {
                        if (!disabled && !readOnly) {
                            setFocused(true)
                            if (onFocus) onFocus()
                            if (!suggestionChosenRef.current) {
                                popoverRef.current?.show()
                            }
                        }
                    }}
                    onBlur={(event) => {
                        event.target.value = value

                        if (required && !value) {
                            setFocused(false)
                            setValue(props.value)
                        } else {
                            setFocused(false)
                            if (typeof onBlur === 'function') props.onBlur(event)
                        }
                        popoverRef.current?.close()
                    }}
                    onClick={props.onClick}
                    onKeyDown={(event) => {
                        if (onKeyDown) onKeyDown(event)

                        if (readOnly) {
                            return
                        }

                        if (popoverRef.current?.closed()) {
                            popoverRef.current?.show()
                        }

                        if (event.key === 'ArrowDown' && shownSuggestions.length) { // Down
                            // to prevent cursor going to the end/right of the input
                            event.preventDefault()
                            if (suggestedIndex < shownSuggestions.length - 1) {
                                setSuggested(suggestedIndex + 1)
                            } else {
                                setSuggested(0)
                            }
                        }

                        if (event.key === 'ArrowUp' && shownSuggestions.length) { // Up
                            // to prevent cursor going to the start/left of the input
                            event.preventDefault()
                            if (suggestedIndex === 0) {
                                setSuggested(shownSuggestions.length - 1)
                            } else {
                                setSuggested(suggestedIndex - 1)
                            }
                        }
                    }}
                    onKeyUp={onKeyUp}
                    onKeyPress={(event) => {
                        if (event.key === 'Enter' && shownSuggestions.length && !readOnly) {
                            if (!popoverRef.current?.closed()) {
                                event.preventDefault()
                            }
                            const suggestion = shownSuggestions[suggestedIndex]
                            chooseSuggestion(typeof suggestion === 'string' ? suggestion : suggestion.value)
                        }
                    }}
                    onChange={handleChange}
                    ref={inputRef}
                    inputMode={inputMode}
                    maxLength={maxLength}
                    aria-label={props['aria-label']}
                />

                {type === 'password' &&
                    <Icon
                        size='lg'
                        style={{marginRight: 6, position: 'absolute', right: 10, top: 10}}
                        icon={showPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'}
                        aria-label={showPassword ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'}
                        onClick={() => setShowPassword(!showPassword)}
                    />
                }

                {/* Without children it renders nothing in place, the list opens at the box (as wide as the box) */}
                {suggestions &&
                    <Popover
                        noClose
                        ref={(ref: PopoverRef | null) => popoverRef.current = ref}
                        anchorRef={boxRef}
                        fitAnchorWidth
                        disabled={!suggestions?.length}
                        content={
                            <div style={{maxHeight: 400, overflowY: 'auto'}} ref={popoverContentRef}>
                                {shownSuggestions.map((suggestion, index) => {
                                    if (typeof suggestion === 'string') {
                                        return (
                                            <ListItem
                                                key={`${index}select${id}`}
                                                focused={suggestedIndex === index}
                                                onMouseDown={() => chooseSuggestion(suggestion)}
                                                id={`suggestion-${index}`}
                                            >
                                                <P ellipsis>
                                                    {boldMatch(suggestion, String(value ?? ''))}
                                                </P>
                                            </ListItem>
                                        )
                                    }

                                    return (
                                        <ListItem
                                            key={`${index}select${id}`}
                                            focused={suggestedIndex === index}
                                            onMouseDown={() => chooseSuggestion(suggestion.value)}
                                            id={`suggestion-${index}`}
                                        >
                                            <P ellipsis>
                                                {boldMatch(suggestion.label || suggestion.value, String(value ?? ''))}
                                            </P>
                                        </ListItem>
                                    )
                                })}
                            </div>
                        }
                    />
                }
            </FieldBox>
        </>
    )
})

export default memo(Input)
